import React, { useEffect, useMemo, useState, useCallback } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import useConversations from 'state/conversations';
import useUser from 'state/user';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import ChatBubbleRoundedIcon from '@mui/icons-material/ChatBubbleRounded';
import PsychologyRoundedIcon from '@mui/icons-material/PsychologyRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded';
import { BRAND_GREEN_PRIMARY } from 'resources/constants/colors';
import { UserTypes } from 'resources/constants/config';

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const NavBar = styled.div`
    display: flex;
    justify-content: center;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 0 0 env(safe-area-inset-bottom, 0);
    z-index: 100;
`;

const NavInner = styled.div`
    display: flex;
    background: #ffffff;
    border-top: 1px solid #f0f0f0;
    justify-content: space-around;
    width: 100%;
    max-width: 480px;
    padding: 8px 0 10px;
`;

const NavItem = styled(Link)`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
    padding: 4px 14px;
    position: relative;
    text-decoration: none;
    -webkit-tap-highlight-color: transparent;

    svg {
        font-size: 26px;
        color: ${({ $active }) => $active ? BRAND_GREEN_PRIMARY : '#bbb'};
        transition: color 0.15s, transform 0.15s;
        ${({ $active }) => $active && `transform: scale(1.1);`}
    }

    &:active svg {
        transform: scale(0.95);
    }
`;

const NavLabel = styled.span`
    font-size: 10px;
    font-weight: ${({ $active }) => $active ? '700' : '500'};
    color: ${({ $active }) => $active ? BRAND_GREEN_PRIMARY : '#bbb'};
    transition: color 0.15s;
`;

const NotifDot = styled.div`
    position: absolute;
    top: 2px;
    right: 10px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #ff4757;
    border: 2px solid #fff;
`;

const ActiveBar = styled.div`
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 24px;
    height: 3px;
    border-radius: 0 0 3px 3px;
    background: ${BRAND_GREEN_PRIMARY};
    opacity: ${({ $active }) => $active ? 1 : 0};
    transition: opacity 0.15s;
`;

// ---------------------------------------------------------------------------
// isCurrent — matches both nested (/home/foo) and top-level (/terapeutas)
// ---------------------------------------------------------------------------

const isCurrent = (to, pathname) => {
    if (to === '/home/resumen') return pathname.startsWith('/home/resumen') || pathname === '/home';
    if (to.startsWith('/home/')) return pathname.startsWith(to);
    return pathname.startsWith(to);
};

// ---------------------------------------------------------------------------
// TABS definition — dinámico por userType y estado de asignación
// ---------------------------------------------------------------------------

const BottomNavBar = ({ location }) => {
    const [conversations] = useConversations();
    const [user] = useUser();

    // Build tabs dynamically based on userType and assignment status
    const TABS = useMemo(() => {
        const userType = user.current?.userType;

        const base = [
            { to: '/home/resumen',    Icon: HomeRoundedIcon,           label: 'Inicio'   },
            { to: '/home/calendario', Icon: CalendarMonthRoundedIcon,  label: 'Agenda'   },
            { to: '/home/mensajes',   Icon: ChatBubbleRoundedIcon,     label: 'Mensajes' },
        ];

        if (userType === UserTypes.CLIENT) {
            // Condicional: tiene terapeuta asignado → "Mi terapeuta" (perfil), si no → "Terapeutas" (listado)
            const hasTherapist = !!user.current?.extraData?.therapist?.id;
            base.push(hasTherapist
                ? { to: '/home/mi-terapeuta', Icon: PsychologyRoundedIcon,  label: 'Mi terapeuta' }
                : { to: '/terapeutas',        Icon: PersonSearchRoundedIcon, label: 'Terapeutas'   }
            );
        }

        if (userType === UserTypes.THERAPIST) {
            base.push({ to: '/home/mis-pacientes', Icon: PeopleAltRoundedIcon, label: 'Mis pacientes' });
        }

        return base;
    }, [user.current?.userType, user.current?.extraData?.therapist]);

    const unreadMessages = useMemo(
        () => conversations.list.some(({ unreadMessagesCount }) => unreadMessagesCount > 0),
        [conversations]
    );

    return (
        <NavBar>
            <NavInner>
                {TABS.map(({ to, Icon, label }) => {
                    const active = isCurrent(to, location.pathname);
                    const showDot = to === '/home/mensajes' && unreadMessages;
                    return (
                        <NavItem key={label} to={to} $active={active}>
                            <ActiveBar $active={active} />
                            <Icon $active={active} />
                            <NavLabel $active={active}>{label}</NavLabel>
                            {showDot && <NotifDot />}
                        </NavItem>
                    );
                })}
            </NavInner>
        </NavBar>
    );
};

export default BottomNavBar;
